import SearchSuggestion from './SearchSuggestion';
import { SearchScenarioId } from '../../store/schema/SearchScenarioId';
import getHighlightedTextForSuggestion from '../../utils/getHighlightedTextForSuggestion';
import highlightDisplayText from '../../utils/highlightDisplayText';
import { observer } from 'mobx-react';
import { IButtonStyles, IconButton } from 'office-ui-fabric-react/lib/Button';
import { DirectionalHint, TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { AttachmentConstants } from 'owa-attachment-file-types/lib/utils/attachmentConstants';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getIconForFile } from 'owa-file-icon';
import { formatRelativeDate } from 'owa-observable-datetime';
import { suggestionSecondaryActionSelected } from 'owa-search';
import { FileSuggestion } from 'owa-search-service';
import * as React from 'react';
import {
    LARGE_SUGGESTION_HEIGHT,
    SMALL_SUGGESTION_HEIGHT,
    CLICK_ACTION_SOURCE,
} from '../../searchConstants';

const styles = require('./FileSearchSuggestion.scss');

export interface FileSearchSuggestionProps {
    index: number;
    scenarioId: SearchScenarioId;
    suggestion: FileSuggestion;
    suggestionSetTraceId: string;
}

@observer
export default class FileSearchSuggestion extends React.Component<FileSearchSuggestionProps, {}> {
    render() {
        const { index, scenarioId, suggestion, suggestionSetTraceId } = this.props;

        const fileIcon = getIconForFile(suggestion.Name);
        const highlightedDisplayText = highlightDisplayText(suggestion.HighlightedDisplayText);
        const highlightedSender = highlightDisplayText(suggestion.Sender);

        const contentContainerStyle: React.CSSProperties = {
            lineHeight: `${LARGE_SUGGESTION_HEIGHT}px`,
        };
        const fileContentContainerStyle: React.CSSProperties = {
            lineHeight: `${SMALL_SUGGESTION_HEIGHT / 2}px`,
        };
        const fileDateTimeReceivedStyle: React.CSSProperties = {
            lineHeight: `${SMALL_SUGGESTION_HEIGHT}px`,
        };

        const isClassicAttachment =
            suggestion.Attachment.__type ===
            AttachmentConstants.attachmentTypes.FILE_ATTACHMENT_TYPE;
        const showQuickActions =
            isFeatureEnabled('sea-fileSuggestionAugmentation') && isClassicAttachment;

        return (
            <SearchSuggestion
                ariaLabel={suggestion.Name}
                content={
                    <div className={styles.container} style={contentContainerStyle}>
                        <div className={styles.fileIconContainer}>
                            <i className={fileIcon.small} />
                        </div>
                        <div
                            className={styles.fileContentContainer}
                            style={fileContentContainerStyle}>
                            <div className={styles.fileDisplayText}>
                                {getHighlightedTextForSuggestion(highlightedDisplayText)}
                            </div>
                            <div className={styles.fileSender}>
                                {getHighlightedTextForSuggestion(highlightedSender)}
                                {showQuickActions && (
                                    <span className={styles.fileDateTimeReceivedWithQuickActions}>
                                        {formatRelativeDate(suggestion.DateTimeReceived)}
                                    </span>
                                )}
                            </div>
                        </div>
                        {!showQuickActions && (
                            <div
                                className={styles.fileDateTimeReceived}
                                style={fileDateTimeReceivedStyle}>
                                {formatRelativeDate(suggestion.DateTimeReceived)}
                            </div>
                        )}
                    </div>
                }
                quickActions={this.getQuickActions(showQuickActions)}
                suggestion={suggestion}
                scenarioId={scenarioId}
                suggestionSetTraceId={suggestionSetTraceId}
                index={index}
            />
        );
    }

    private getQuickActions = (showQuickActions: boolean) => {
        if (!showQuickActions) {
            return [];
        }

        const quickActions = [];
        const quickActionButtonStyles: IButtonStyles = {
            root: {
                height: LARGE_SUGGESTION_HEIGHT,
            },
        };

        quickActions.push(
            // TODO: Localize tooltip content string
            <TooltipHost
                content="Share file by email"
                directionalHint={DirectionalHint.topCenter}
                id="shareFileButton">
                <IconButton
                    iconProps={{
                        iconName: ControlIcons.Mail,
                    }}
                    className={styles.fileQuickActionButton}
                    onClick={this.shareFromSearch}
                    aria-describedby="shareFileButton"
                    styles={quickActionButtonStyles}
                />
            </TooltipHost>,
            // TODO: Localize tooltip content string
            <TooltipHost
                content="Download file"
                directionalHint={DirectionalHint.topCenter}
                id="downloadFileButton">
                <IconButton
                    iconProps={{
                        iconName: ControlIcons.Download,
                    }}
                    className={styles.fileQuickActionButton}
                    onClick={this.downloadFromSearch}
                    aria-describedby="downloadFileButton"
                    styles={quickActionButtonStyles}
                />
            </TooltipHost>
        );

        return quickActions;
    };

    private shareFromSearch = ev => {
        ev.stopPropagation();

        const { suggestion, scenarioId, index, suggestionSetTraceId } = this.props;

        suggestionSecondaryActionSelected(
            suggestion,
            index,
            CLICK_ACTION_SOURCE,
            scenarioId,
            suggestionSetTraceId,
            'Share' /*EntityActionTakenType*/
        );
    };

    private downloadFromSearch = ev => {
        ev.stopPropagation();

        const { suggestion, scenarioId, index, suggestionSetTraceId } = this.props;

        suggestionSecondaryActionSelected(
            suggestion,
            index,
            CLICK_ACTION_SOURCE,
            scenarioId,
            suggestionSetTraceId,
            'Download' /*EntityActionTakenType*/
        );
    };
}
